import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useModal } from '../../contexts/ModalContext'
import { useAuth } from '../../contexts/AuthContext'
import { useCampaign } from '../../contexts/CampaignContext'
import SecretForm from '../forms/SecretForm'
import AssignSecretModal from '../AssignSecretModal'
import CharacterForm from '../forms/CharacterForm'
import MarkdownText from '../MarkdownText'
import CharacterJournal from './CharacterJournal'

function MyCharacterTab({ initialSubTab }) {
  const [activeSubTab, setActiveSubTab] = useState(initialSubTab || 'character') // 'character' or 'secrets' or 'journal' or 'admin-secrets'
  const [character, setCharacter] = useState(null)
  const [allCharacters, setAllCharacters] = useState([]) // For admin dropdown
  const [selectedCharacterId, setSelectedCharacterId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isEditingCharacter, setIsEditingCharacter] = useState(false)
  // null = not checked yet, so the button renders neither enabled nor as a
  // definitive "no access" until the answer is actually in.
  const [feybloodAccess, setFeybloodAccess] = useState(null)
  const { user, authEpoch } = useAuth()
  const { campaign, canEdit, isModuleEnabled } = useCampaign()
  const { alert: showAlert } = useModal()
  const navigate = useNavigate()

  // Update activeSubTab when initialSubTab prop changes
  useEffect(() => {
    if (initialSubTab) {
      setActiveSubTab(initialSubTab)
    }
  }, [initialSubTab])

  // Whether this user may enter the Feyblood page. Access is a per-user grant
  // the DM makes in campaign settings, so the button below is greyed out
  // rather than letting a player click through to an Access Denied screen.
  // authEpoch: see the identical comment in ItemsTab.jsx.
  useEffect(() => {
    if (!user || !campaign || !isModuleEnabled('feyblood')) return
    let cancelled = false
    fetch('/api/feyblood/access')
      .then(res => (res.ok ? res.json() : { hasAccess: false }))
      .then(data => {
        if (!cancelled) setFeybloodAccess(!!data.hasAccess)
      })
      .catch(() => {
        if (!cancelled) setFeybloodAccess(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, campaign?.id, authEpoch])

  // Fetch character data. authEpoch: see the identical comment in
  // ItemsTab.jsx — refetches if this tab was already mounted (and already
  // landed on the `error || !character` screen below) when the session
  // expired.
  useEffect(() => {
    if (!user) return

    if (canEdit) {
      // Admin: Fetch all characters for dropdown
      fetch('/api/character') // Will need to update this endpoint to return all characters for admin
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            setAllCharacters(data)
            setError(null)
            if (data.length > 0) {
              // Preserve the admin's current selection across a refetch
              // (e.g. the authEpoch-triggered retry after re-authenticating)
              // when that character is still present in the new data —
              // unconditionally resetting to data[0] here would silently
              // swap the selection out from under an admin who had picked a
              // different character, which is exactly the "signing in again
              // must not lose what you were doing" failure this change
              // exists to prevent. Falls back to the first entry when there
              // was no prior selection (initial mount) or the previously
              // selected character is no longer in the list.
              const preserved = data.find(c => c.id === selectedCharacterId)
              const next = preserved || data[0]
              setSelectedCharacterId(next.id)
              setCharacter(next)
            }
          } else {
            // Non-OK responses (e.g. a 401) still have a JSON body, just not
            // an array — surface it as an error instead of silently doing
            // nothing, which would otherwise leave a stale error/empty state
            // on screen after a failed retry.
            setError(data?.error || 'Failed to load characters')
          }
          setLoading(false)
        })
        .catch(err => {
          setError(err.message)
          setLoading(false)
        })
    } else {
      // Player: Fetch only their character
      fetch('/api/character')
        .then(res => res.json())
        .then(data => {
          if (data.error) {
            setError(data.error)
          } else {
            setCharacter(data)
            setError(null)
          }
          setLoading(false)
        })
        .catch(err => {
          setError(err.message)
          setLoading(false)
        })
    }
  }, [user, canEdit, authEpoch])

  // Update character when selection changes (admin only)
  const handleCharacterSelect = (e) => {
    const characterId = parseInt(e.target.value)
    setSelectedCharacterId(characterId)
    const selectedChar = allCharacters.find(c => c.id === characterId)
    setCharacter(selectedChar)
  }

  const handleCharacterFormSubmit = async (formData, portraitFile, deletePortrait) => {
    try {
      let appearance_image_url = character.appearance_image_url

      // Handle portrait deletion
      if (deletePortrait && character.appearance_image_url) {
        const deleteResponse = await fetch('/api/character/delete-portrait', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ character_id: character.id })
        })

        if (deleteResponse.ok) {
          appearance_image_url = null
        }
      }

      // Handle portrait upload
      if (portraitFile) {
        const uploadFormData = new FormData()
        uploadFormData.append('portrait', portraitFile)
        uploadFormData.append('character_id', character.id)

        const uploadResponse = await fetch('/api/character/upload-portrait', {
          method: 'POST',
          body: uploadFormData
        })

        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json()
          appearance_image_url = uploadData.appearance_image_url
        }
      }

      // Update character data
      const response = await fetch('/api/character', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          character_id: character.id,
          ...formData,
          appearance_image_url
        })
      })

      if (response.ok) {
        // Refresh character data
        setCharacter(prev => ({
          ...prev,
          ...formData,
          appearance_image_url
        }))

        // If admin, also update allCharacters
        if (canEdit) {
          setAllCharacters(prev => prev.map(c =>
            c.id === character.id ? { ...c, ...formData, appearance_image_url } : c
          ))
        }

        showAlert({
          title: 'Success',
          message: 'Character updated successfully',
          type: 'success'
        })
        setIsEditingCharacter(false)
      } else {
        const error = await response.json()
        showAlert({
          title: 'Error',
          message: error.error || 'Failed to update character',
          type: 'error'
        })
      }
    } catch (err) {
      showAlert({
        title: 'Error',
        message: 'Failed to update character',
        type: 'error'
      })
    }
  }

  if (loading) {
    return (
      <div className="tab-pane">
        <div className="page-header">
          <h1>My Character</h1>
          <p className="page-subtitle">Your character's story and secrets</p>
        </div>
        <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-muted)' }}>Loading character...</p>
        </div>
      </div>
    )
  }

  if (!character && !canEdit) {
    return (
      <div className="tab-pane">
        <div className="page-header">
          <h1>My Character</h1>
          <p className="page-subtitle">Your character's story and secrets</p>
        </div>
        <div className="character-empty" style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>🎭</div>
          <h3 style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>No Character Yet</h3>
          <p style={{ color: 'var(--text-muted)' }}>You don't have a character in this campaign. Ask your DM to add one.</p>
        </div>
      </div>
    )
  }

  if (error || !character) {
    return (
      <div className="tab-pane">
        <div className="page-header">
          <h1>My Character</h1>
          <p className="page-subtitle">Your character's story and secrets</p>
        </div>
        <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>🎭</div>
          <h3 style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>No Character Created</h3>
          <p style={{ color: 'var(--text-muted)' }}>
            {canEdit
              ? 'No players have a character in this campaign yet. Add members from Campaign Settings to get started.'
              : 'Your character information will appear here once created by the DM.'}
          </p>
        </div>
      </div>
    )
  }

  // Sub-tab navigation styles
  const subTabNavStyle = {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    borderBottom: '2px solid var(--border-color)',
    flexWrap: 'wrap'
  }

  const subTabButtonStyle = (isActive) => ({
    padding: '0.75rem 1.5rem',
    background: 'transparent',
    border: 'none',
    borderBottom: isActive ? '2px solid var(--accent-teal)' : '2px solid transparent',
    color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
    fontSize: '1rem',
    fontWeight: isActive ? 600 : 400,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
    marginBottom: '-2px'
  })

  return (
    <div className="tab-pane">
      <div className="page-header">
        <h1>{canEdit ? 'Character Management' : character.character_name}</h1>
        <p className="page-subtitle">{canEdit ? 'View player characters and manage secrets' : "Your character's story"}</p>
      </div>

      {/* Admin character selector */}
      {canEdit && allCharacters.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '0.5rem', fontSize: '0.95rem', fontWeight: 500 }}>
            Select Character:
          </label>
          <select
            value={selectedCharacterId || ''}
            onChange={handleCharacterSelect}
            style={{
              width: '100%',
              maxWidth: '400px',
              padding: '0.75rem',
              background: 'var(--accent-deep)',
              border: '1px solid var(--accent-teal)',
              borderRadius: '6px',
              color: 'var(--text-primary)',
              fontSize: '0.95rem',
              cursor: 'pointer'
            }}
          >
            {allCharacters.map(char => (
              <option key={char.id} value={char.id}>
                {char.character_name} ({char.username})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Sub-tab navigation */}
      <div style={subTabNavStyle}>
        <button
          style={subTabButtonStyle(activeSubTab === 'character')}
          onClick={() => setActiveSubTab('character')}
        >
          {canEdit ? `${character.character_name}'s Character` : 'Character'}
        </button>
        <button
          style={subTabButtonStyle(activeSubTab === 'secrets')}
          onClick={() => setActiveSubTab('secrets')}
        >
          Secrets & Powers
        </button>
        <button
          style={subTabButtonStyle(activeSubTab === 'journal')}
          onClick={() => setActiveSubTab('journal')}
        >
          📓 Journal
        </button>
        {canEdit && (
          <button
            style={subTabButtonStyle(activeSubTab === 'admin-secrets')}
            onClick={() => setActiveSubTab('admin-secrets')}
          >
            Manage All Secrets
          </button>
        )}
      </div>

      {/* Character Tab */}
      {activeSubTab === 'character' && (
        <div>
          {/* Character name + edit button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ color: 'var(--text-primary)', fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', fontWeight: 600 }}>
              {character.character_name || user?.username || 'Unnamed Character'}
            </div>
            <button
              onClick={() => setIsEditingCharacter(true)}
              style={{
                background: 'linear-gradient(135deg, #1a5a6e 0%, #2a7a8e 100%)',
                color: '#ffffff',
                border: '1px solid var(--accent-teal)',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(42, 85, 102, 0.3)',
              }}
            >
              ✏️ Edit Character
            </button>
          </div>

          {/* Portrait + Appearance Section */}
          {(character.appearance_image_url || character.appearance) && (
            <div style={{
              display: 'flex',
              gap: '2rem',
              marginBottom: '2rem',
              flexWrap: 'wrap'
            }}>
              {/* Character Portrait */}
              {character.appearance_image_url && (
                <div style={{
                  background: 'var(--card-bg)',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  border: '1px solid var(--border-color)',
                  flexShrink: 0,
                  alignSelf: 'flex-start'
                }}>
                  <img
                    src={character.appearance_image_url}
                    alt={character.character_name}
                    style={{
                      width: '280px',
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                      display: 'block'
                    }}
                  />
                </div>
              )}

              {/* Appearance */}
              {character.appearance && (
                <div style={{
                  background: 'var(--card-bg)',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid var(--border-color)',
                  flex: 1,
                  minWidth: '280px'
                }}>
                  <h3 style={{ color: 'var(--text-primary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>👁️</span> Appearance
                  </h3>
                  <MarkdownText style={{ color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{character.appearance}</MarkdownText>
                </div>
              )}
            </div>
          )}

          {/* Personality */}
          {character.personality && (
            <div style={{ background: 'var(--card-bg)', padding: '2rem', borderRadius: '12px', border: '1px solid var(--border-color)', marginBottom: '2rem' }}>
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span>💭</span> Personality
              </h3>
              <MarkdownText style={{ color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{character.personality}</MarkdownText>
            </div>
          )}

          {/* Backstory */}
          {character.backstory && (
            <div style={{ background: 'var(--card-bg)', padding: '2rem', borderRadius: '12px', border: '1px solid var(--border-color)', marginBottom: '2rem' }}>
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span>📖</span> Backstory
              </h3>
              <MarkdownText style={{ color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{character.backstory}</MarkdownText>
            </div>
          )}

          {!character.appearance && !character.personality && !character.backstory && (
            <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>📝</div>
              <h3 style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>No Character Details</h3>
              <p style={{ color: 'var(--text-muted)' }}>Character details will appear here once added by the DM.</p>
            </div>
          )}
        </div>
      )}

      {/* Secrets & Powers Tab */}
      {activeSubTab === 'secrets' && (
        <div>
          {isModuleEnabled('feyblood') && campaign && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
              flexWrap: 'wrap',
              background: 'linear-gradient(135deg, #1a3a45 0%, #0d1520 100%)',
              border: '1px solid var(--accent-teal)',
              borderRadius: '12px',
              padding: '1.25rem 1.5rem',
              marginBottom: '1.5rem',
            }}>
              <div>
                <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '1.05rem', marginBottom: '0.25rem' }}>
                  🍄 Feyblood Powers
                </div>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: 0 }}>
                  {feybloodAccess === false
                    ? 'Your DM has not granted you access to the Feyblood system.'
                    : 'Roll the d100 to discover Feywild-touched effects.'}
                </p>
              </div>
              {/* `feybloodAccess === null` is the in-flight state: the button
                  is disabled but keeps its normal look, so a granted player
                  never sees it flash "Locked" before the check comes back. */}
              <button
                onClick={() => navigate(`/c/${campaign.slug}/feyblood`)}
                disabled={!feybloodAccess}
                title={
                  feybloodAccess === false
                    ? 'Your DM has not granted you access to the Feyblood system.'
                    : undefined
                }
                style={{
                  background: feybloodAccess === false
                    ? 'var(--hover-bg)'
                    : 'linear-gradient(135deg, #1a5a6e 0%, #2a7a8e 100%)',
                  color: feybloodAccess === false ? 'var(--text-muted)' : '#ffffff',
                  border: `1px solid ${feybloodAccess === false ? 'var(--border-color)' : 'var(--accent-teal)'}`,
                  padding: '0.65rem 1.25rem',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  cursor: feybloodAccess ? 'pointer' : 'not-allowed',
                  opacity: feybloodAccess === false ? 0.6 : 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {feybloodAccess === false ? '🔒 Feyblood Locked' : 'Go to Feyblood →'}
              </button>
            </div>
          )}
          {character.secrets && character.secrets.length > 0 ? (
            <div style={{ background: 'linear-gradient(135deg, #1a3a45 0%, #0d1520 100%)', padding: '2rem', borderRadius: '12px', border: '2px solid var(--accent-teal)' }}>
              <h3 style={{ color: 'var(--accent-teal)', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.5rem' }}>
                <span>🔐</span> {canEdit ? `${character.character_name}'s Secrets` : 'Your Secrets'}
              </h3>
              <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem', fontSize: '0.9rem', fontStyle: 'italic' }}>
                {canEdit ? 'Secrets assigned to this character.' : 'These are secrets only you know. Guard them carefully...'}
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {character.secrets.map((secret, index) => (
                  <div key={secret.id} style={{ background: 'var(--card-bg)', padding: '1.5rem', borderRadius: '10px', border: '1px solid var(--accent-teal)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                      {secret.is_mystery === 1 ? (
                        <div style={{ fontSize: '2rem' }}>❓</div>
                      ) : (
                        <div style={{ fontSize: '2rem' }}>✨</div>
                      )}
                      <h4 style={{ color: 'var(--text-primary)', fontSize: '1.25rem', margin: 0 }}>{secret.name}</h4>
                    </div>

                    <div style={{ color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '1rem' }}>
                      <MarkdownText>{secret.description}</MarkdownText>
                    </div>

                    {secret.benefits && (
                      <div style={{ background: 'rgba(42, 85, 102, 0.2)', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                        <div style={{ color: 'var(--accent-teal)', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                          ⚡ Benefits:
                        </div>
                        <div style={{ color: 'var(--text-secondary)', lineHeight: 1.6, fontSize: '0.95rem', margin: 0 }}>
                          <MarkdownText>{secret.benefits}</MarkdownText>
                        </div>
                      </div>
                    )}

                    {secret.drawbacks && (
                      <div style={{ background: 'rgba(192, 57, 43, 0.15)', padding: '1rem', borderRadius: '8px' }}>
                        <div style={{ color: '#e74c3c', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                          ⚠️ Drawbacks:
                        </div>
                        <div style={{ color: 'var(--text-secondary)', lineHeight: 1.6, fontSize: '0.95rem', margin: 0 }}>
                          <MarkdownText>{secret.drawbacks}</MarkdownText>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>🔐</div>
              <h3 style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>No Secrets Assigned</h3>
              <p style={{ color: 'var(--text-muted)' }}>
                {canEdit ? 'This character has no secrets assigned yet.' : 'You have no secrets yet. They will appear here when the DM assigns them.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Journal Tab */}
      {activeSubTab === 'journal' && (
        <CharacterJournal characterId={character?.id} canEdit={canEdit} />
      )}

      {/* Admin Secrets Management Tab */}
      {activeSubTab === 'admin-secrets' && canEdit && (
        <AdminSecretsView />
      )}

      {/* Character Edit Form Modal */}
      <CharacterForm
        isOpen={isEditingCharacter}
        onClose={() => setIsEditingCharacter(false)}
        character={character}
        onSubmit={handleCharacterFormSubmit}
      />
    </div>
  )
}

// Admin view for managing all secrets
function AdminSecretsView() {
  const [secrets, setSecrets] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingSecret, setEditingSecret] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [assigningSecret, setAssigningSecret] = useState(null)
  const { alert: showAlert, confirm } = useModal()
  const { authEpoch } = useAuth()

  // authEpoch: see the identical comment in ItemsTab.jsx.
  useEffect(() => {
    fetchSecrets()
  }, [authEpoch])

  const fetchSecrets = () => {
    // See ItemsTab.jsx's fetchItems: a non-OK response still has a JSON
    // body, not an array — guard against handing it to setSecrets().
    fetch('/api/secrets')
      .then(res => (res.ok ? res.json() : Promise.reject(new Error('Failed to load secrets'))))
      .then(data => {
        setSecrets(Array.isArray(data) ? data : [])
        setLoading(false)
      })
      .catch(err => {
        console.error(err)
        setLoading(false)
      })
  }

  const handleEdit = (secret) => {
    setEditingSecret(secret)
  }

  const handleCreate = () => {
    setIsCreating(true)
  }

  const handleFormSubmit = async (formData) => {
    try {
      const isEdit = !!editingSecret
      const response = await fetch('/api/secrets', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isEdit ? { ...formData, id: editingSecret.id } : formData)
      })

      if (response.ok) {
        await fetchSecrets()
        showAlert({
          title: isEdit ? 'Secret Updated' : 'Secret Created',
          message: `Secret "${formData.name}" has been ${isEdit ? 'updated' : 'created'} successfully.`,
          type: 'success'
        })
        setEditingSecret(null)
        setIsCreating(false)
      } else {
        const error = await response.json()
        showAlert({
          title: 'Error',
          message: error.error || `Failed to ${isEdit ? 'update' : 'create'} secret`,
          type: 'error'
        })
      }
    } catch (err) {
      showAlert({
        title: 'Error',
        message: `Failed to ${editingSecret ? 'update' : 'create'} secret`,
        type: 'error'
      })
    }
  }

  const handleDelete = async (secret) => {
    await confirm({
      title: 'Delete Secret',
      message: `Are you sure you want to delete "${secret.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          const response = await fetch('/api/secrets', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: secret.id })
          })

          if (response.ok) {
            setSecrets(secrets.filter(s => s.id !== secret.id))
            showAlert({
              title: 'Secret Deleted',
              message: `"${secret.name}" has been deleted.`,
              type: 'success'
            })
          } else {
            const error = await response.json()
            showAlert({
              title: 'Error',
              message: error.error || 'Failed to delete secret',
              type: 'error'
            })
          }
        } catch (err) {
          showAlert({
            title: 'Error',
            message: 'Failed to delete secret',
            type: 'error'
          })
        }
      }
    })
  }

  const handleAssignSecret = async (assignmentData) => {
    try {
      const response = await fetch('/api/secrets/assign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(assignmentData)
      })

      if (response.ok) {
        await fetchSecrets()
        showAlert({
          title: 'Secret Assigned',
          message: 'Secret has been assigned to the character successfully.',
          type: 'success'
        })
        setAssigningSecret(null)
      } else {
        const error = await response.json()
        showAlert({
          title: 'Error',
          message: error.error || 'Failed to assign secret',
          type: 'error'
        })
      }
    } catch (err) {
      showAlert({
        title: 'Error',
        message: 'Failed to assign secret',
        type: 'error'
      })
      throw err
    }
  }

  const handleUnassignSecret = async (secretId, characterId, secretName, characterName) => {
    await confirm({
      title: 'Unassign Secret',
      message: `Remove "${secretName}" from ${characterName}?`,
      confirmText: 'Unassign',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          const response = await fetch('/api/secrets/assign', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ secret_id: secretId, character_id: characterId })
          })

          if (response.ok) {
            await fetchSecrets()
            showAlert({
              title: 'Secret Unassigned',
              message: `"${secretName}" has been removed from ${characterName}.`,
              type: 'success'
            })
          } else {
            const error = await response.json()
            showAlert({
              title: 'Error',
              message: error.error || 'Failed to unassign secret',
              type: 'error'
            })
          }
        } catch (err) {
          showAlert({
            title: 'Error',
            message: 'Failed to unassign secret',
            type: 'error'
          })
        }
      }
    })
  }

  if (loading) {
    return (
      <div style={{ background: 'var(--card-bg)', padding: '3rem', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
        <p style={{ color: 'var(--text-muted)' }}>Loading secrets...</p>
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>All Secrets Management</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem' }}>Create and assign secrets to players</p>
        </div>
        <button
          onClick={handleCreate}
          style={{
            background: 'linear-gradient(135deg, #1a8a6e 0%, #2ecc71 100%)',
            color: '#ffffff',
            border: '1px solid #27ae60',
            padding: '0.8rem 1.5rem',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '0.95rem',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(46, 204, 113, 0.3)',
          }}
        >
          + Create New Secret
        </button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--card-bg)', borderRadius: '12px', overflow: 'hidden' }}>
          <thead>
            <tr style={{ background: 'var(--accent-deep)', borderBottom: '2px solid var(--accent-teal)' }}>
              <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9rem' }}>Name</th>
              <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9rem' }}>Description</th>
              <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9rem' }}>Benefits</th>
              <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9rem' }}>Drawbacks</th>
              <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9rem' }}>Assigned To</th>
              <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9rem' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {secrets.map(secret => (
              <tr key={secret.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: '1rem', verticalAlign: 'top' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {secret.is_mystery === 1 ? '❓' : '✨'}
                    <div>
                      <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{secret.name}</div>
                      {secret.is_mystery === 1 && (
                        <span style={{ background: '#6b7f91', color: 'var(--text-primary)', padding: '0.15rem 0.4rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600 }}>
                          Mystery
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td style={{ padding: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.5, verticalAlign: 'top', maxWidth: '300px' }}>
                  <MarkdownText>{secret.description}</MarkdownText>
                </td>
                <td style={{ padding: '1rem', color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: 1.5, verticalAlign: 'top', maxWidth: '250px' }}>
                  {secret.benefits ? <MarkdownText>{secret.benefits}</MarkdownText> : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>None</span>}
                </td>
                <td style={{ padding: '1rem', color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: 1.5, verticalAlign: 'top', maxWidth: '250px' }}>
                  {secret.drawbacks ? <MarkdownText>{secret.drawbacks}</MarkdownText> : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>None</span>}
                </td>
                <td style={{ padding: '1rem', verticalAlign: 'top' }}>
                  {secret.assignments && secret.assignments.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {secret.assignments.map(assignment => (
                        <div key={assignment.character_id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'var(--accent-deep)', padding: '0.4rem 0.6rem', borderRadius: '4px' }}>
                          <span style={{ color: 'var(--text-primary)', fontSize: '0.85rem' }}>
                            {assignment.character_name} ({assignment.username})
                          </span>
                          <button
                            onClick={() => handleUnassignSecret(secret.id, assignment.character_id, secret.name, assignment.character_name)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: '#e74c3c',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              padding: '0.2rem',
                              lineHeight: 1
                            }}
                            title="Unassign"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontStyle: 'italic' }}>Not assigned</span>
                  )}
                </td>
                <td style={{ padding: '1rem', verticalAlign: 'top' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <button
                      onClick={() => setAssigningSecret(secret)}
                      style={{
                        background: 'linear-gradient(135deg, #1a8a6e 0%, #2ecc71 100%)',
                        color: '#ffffff',
                        border: '1px solid #27ae60',
                        padding: '0.5rem 0.75rem',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 4px rgba(46, 204, 113, 0.2)'
                      }}
                    >
                      Assign
                    </button>
                    <button
                      onClick={() => handleEdit(secret)}
                      style={{
                        background: 'linear-gradient(135deg, #1a5a6e 0%, #2a7a8e 100%)',
                        color: '#ffffff',
                        border: '1px solid var(--accent-teal)',
                        padding: '0.5rem 0.75rem',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        fontWeight: 600,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(secret)}
                      style={{
                        background: 'linear-gradient(135deg, #a93226 0%, #c0392b 100%)',
                        color: '#ffffff',
                        border: '1px solid #e74c3c',
                        padding: '0.5rem 0.75rem',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        fontWeight: 600,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SecretForm
        isOpen={isCreating || !!editingSecret}
        onClose={() => {
          setIsCreating(false)
          setEditingSecret(null)
        }}
        secret={editingSecret}
        onSubmit={handleFormSubmit}
      />

      <AssignSecretModal
        isOpen={!!assigningSecret}
        onClose={() => setAssigningSecret(null)}
        secret={assigningSecret}
        onAssign={handleAssignSecret}
      />
    </div>
  )
}

export default MyCharacterTab
